import { ApolloError } from '@apollo/client';
import { t } from 'i18next';
import moment from 'moment';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { Route, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import {
	ArchiveIcon,
	CalendarIcon,
	ChevronDownIcon,
	FriendsIcon,
	PrintIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	CircleCheckmarkIcon,
	Color,
	ContextMenu,
	FlexGap,
	IconWrapper,
	Paragraph,
	Spinner,
	SyncIcon,
	WarningIcon,
	tokens,
	useAppScrollElement,
} from '@treams/treams-ui';

import { LayoutFooter } from '../../../context-sidebar';
import ChangeConversationDateModal from '../ChangeConversationDateModal';
import ChangeConversationPartnerModal from '../ChangeConversationPartnerModal';
import EvaluationSubmissionExportModal from './EvaluationSubmissionExportModal';
import EvaluationSubmissionShareModal from './EvaluationSubmissionShareModal';
import { shouldWaitForPartnerShare } from './EvaluationSubmissionStatus';

const Footer = styled(Box)`
	border-top: 1px solid ${tokens.border.subtle};
	background: ${tokens.surface.white};
	flex-shrink: 0;
	width: 100%;
	box-sizing: border-box;
	padding: ${tokens.padding.compact}px 100px ${tokens.padding.compact}px
		${tokens.padding.container}px;
`;

const STATUS_ICON_SIZE = tokens.typography.sizes.body;

const Status = styled(FlexGap)`
	align-items: center;
`;

const StatusIcon = styled(IconWrapper).attrs({
	size: STATUS_ICON_SIZE,
})`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	overflow: hidden;

	svg {
		display: block;
		width: ${STATUS_ICON_SIZE}px;
		height: ${STATUS_ICON_SIZE}px;
	}

	/* Spinner is a Flex wrapping a 16px disc */
	> div,
	> div > div {
		width: ${STATUS_ICON_SIZE}px !important;
		height: ${STATUS_ICON_SIZE}px !important;
	}
`;

const StatusText = styled(Paragraph)`
	line-height: ${tokens.typography.lineHeights.body};
`;

function ConversationActionsButton(props: {
	submission: graphql.EvaluationSubmission;
	onChangeDate: () => void;
	onChangePartner: () => void;
	onExport: () => void;
}) {
	const { t } = useTranslation();
	const [archive, { loading: archiveLoading }] = graphql.useArchiveEvaluationSubmissionMutation({
		variables: { input: { id: props.submission.id } },
	});
	const [restore, { loading: restoreLoading }] = graphql.useRestoreEvaluationSubmissionMutation({
		variables: { input: { id: props.submission.id } },
	});

	return (
		<ContextMenu.Target
			menu={
				<ContextMenu width={260}>
					{props.submission.viewerCanChangeConversationPartner && (
						<ContextMenu.Item
							title={t('evaluation.submission.changeConversationPartner.button')}
							icon={<FriendsIcon />}
							onClick={() => props.onChangePartner()}
						/>
					)}

					<ContextMenu.Item
						title={t('evaluation.submission.changeDate.button')}
						icon={<CalendarIcon />}
						onClick={() => props.onChangeDate()}
					/>

					<ContextMenu.Item
						title={t('evaluation.submission.print')}
						icon={<PrintIcon />}
						onClick={() => props.onExport()}
					/>

					{props.submission.viewer.viewerCanArchive && (
						<>
							<ContextMenu.Separator />
							<ContextMenu.Item
								title={
									archiveLoading
										? t('evaluation.submission.archiveRestore.archiving')
										: restoreLoading
											? t('evaluation.submission.archiveRestore.restoring')
											: props.submission.isArchived
												? t('evaluation.submission.archiveRestore.restore')
												: t('evaluation.submission.archiveRestore.archive')
								}
								icon={<ArchiveIcon />}
								type="destructive"
								onClick={async () => {
									if (props.submission.isArchived) {
										await restore();
									} else {
										await archive();
									}
								}}
							/>
						</>
					)}
				</ContextMenu>
			}
		>
			<Flex flexShrink={0}>
				<Button
					small
					title={t('evaluation.submission.actions.button')}
					mr={2}
					type="secondary"
					rightIcon={<ChevronDownIcon />}
				/>
			</Flex>
		</ContextMenu.Target>
	);
}

function ConversationSaveStatusError(props: {
	submission: graphql.EvaluationSubmission;
	onSave: () => void;
}) {
	const { t } = useTranslation();

	return (
		<Status gap={tokens.gap.element}>
			<StatusIcon color={tokens.error.icon}>
				<WarningIcon size={STATUS_ICON_SIZE} color={tokens.error.icon} />
			</StatusIcon>
			<StatusText display={['none', 'none', 'block', 'block']} color={'red'}>
				{t('evaluations.submission.autosave.error')}
			</StatusText>
			<Box ml={3}>
				{props.submission.viewer.viewerCanUpdate && (
					<Color color="red">
						<Button
							title={t('evaluations.submission.autosave.tryAgain')}
							onClick={props.onSave}
							type="plain"
							small
						/>
					</Color>
				)}
			</Box>
		</Status>
	);
}

function ConversationAutoSaveStatus(
	props: PropsWithChildren<{
		submission: graphql.EvaluationSubmission;
		saving: boolean;
		lastSaved: Date | null;
	}>,
) {
	return (
		<Status gap={tokens.gap.element}>
			<StatusIcon>
				{props.saving ? (
					<Spinner width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
				) : (
					<SyncIcon size={STATUS_ICON_SIZE} />
				)}
			</StatusIcon>
			{props.lastSaved && (
				<StatusText display={['none', 'none', 'block', 'block']}>
					{t('evaluations.submission.autosave.concept')}
					&nbsp;
					<Moment interval={5000} fromNow>
						{moment(props.lastSaved)}
					</Moment>
				</StatusText>
			)}
		</Status>
	);
}

function ConversationSaveStatus(
	props: PropsWithChildren<{
		submission: graphql.EvaluationSubmission;
		saving: boolean;
		lastSaved: Date | null;
	}>,
) {
	return (
		<Status gap={tokens.gap.element}>
			<StatusIcon>
				{props.saving ? (
					<Spinner width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
				) : (
					<CircleCheckmarkIcon size={STATUS_ICON_SIZE} />
				)}
			</StatusIcon>
			{props.lastSaved && (
				<StatusText display={['none', 'none', 'block', 'block']}>
					{t('evaluations.submission.autosave.submission')}
					&nbsp;
					<Moment interval={5000} fromNow>
						{moment(props.lastSaved)}
					</Moment>
				</StatusText>
			)}
		</Status>
	);
}

export default function EvaluationSubmissionFooter(props: {
	submission: graphql.EvaluationSubmission;
	error?: Error;
	mutationError?: ApolloError;
	saving: boolean;
	sharing: boolean;
	lastSaved: Date | null;
	onShare: () => void;
	onSign: () => void;
	onSave: () => void;
}) {
	const { t } = useTranslation();
	const [modal, setModal] = useState<'share' | 'partner' | 'export' | null>(null);

	const history = useHistory();
	const viewer = useAuthenticatedUser();
	const scroller = useAppScrollElement();

	const handleDismiss = () => {
		setModal(null);
		scroller?.scrollTo(0, 0);
	};

	return (
		<LayoutFooter>
			<Footer>
				{modal === 'share' && (
					<EvaluationSubmissionShareModal
						submission={props.submission}
						onShare={props.onShare}
						sharing={props.sharing}
						onDismiss={handleDismiss}
					/>
				)}
				{modal === 'export' && (
					<EvaluationSubmissionExportModal
						submissionID={props.submission.id}
						onDismiss={handleDismiss}
					/>
				)}
				<Route
					path={[`/users/:userID/conversations/:id/change-date`, `/conversations/:id/change-date`]}
				>
					{({ match }) =>
						!!match && (
							<ChangeConversationDateModal
								id={props.submission.id}
								deadline={props.submission.deadline}
								duration={props.submission.meeting?.duration}
								location={props.submission.meeting?.location}
								onDismiss={() =>
									history.push(
										`/users/${props.submission.employee.user.id}/conversations/${props.submission.id}`.replace(
											`/users/${viewer.id}`,
											'',
										),
									)
								}
							/>
						)
					}
				</Route>
				{modal === 'partner' && (
					<ChangeConversationPartnerModal
						id={props.submission.id}
						employeeID={props.submission.employee.user.id}
						userID={props.submission.manager.user.id}
						enabled={props.submission.viewerCanChangeConversationPartner}
						onDismiss={handleDismiss}
					/>
				)}

				<Flex justifyContent="space-between" alignItems="center">
					{props.submission.viewer.viewerCanArchive && (
						<>
							{!props.error && !props.mutationError && props.submission.viewer.hasShared && (
								<ConversationSaveStatus
									saving={props.saving}
									submission={props.submission}
									lastSaved={props.lastSaved}
								/>
							)}
							{!props.error && !props.mutationError && !props.submission.viewer.hasShared && (
								<ConversationAutoSaveStatus
									saving={props.saving}
									submission={props.submission}
									lastSaved={props.lastSaved}
								/>
							)}

							{(props.error || props.mutationError) && (
								<ConversationSaveStatusError submission={props.submission} onSave={props.onSave} />
							)}
						</>
					)}

					<Flex>
						<ConversationActionsButton
							submission={props.submission}
							onChangeDate={() =>
								history.push(
									`/users/${props.submission.employee.user.id}/conversations/${props.submission.id}/change-date`.replace(
										`/users/${viewer.id}`,
										'',
									),
								)
							}
							onChangePartner={() => {
								setModal('partner');
							}}
							onExport={() => {
								setModal('export');
							}}
						/>

						{props.submission.viewer.viewerCanShare && (
							<Button
								small
								title={t('evaluation.submission.save.share')}
								onClick={() => {
									setModal('share');
								}}
								flexShrink={0}
							/>
						)}

						{props.submission.viewer.viewerCanSign &&
							props.submission.viewer.viewerCanUpdate &&
							!shouldWaitForPartnerShare(props.submission) && (
								<Button
									small
									title={t('evaluation.submission.confirm.button')}
									onClick={() => props.onSign()}
									flexShrink={0}
								/>
							)}
					</Flex>
				</Flex>
			</Footer>
		</LayoutFooter>
	);
}
